// src/pages/AlarmDetails/tabs/HistoryTab.jsx
// History tab showing detailed timeline of alarm events

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar,
  Grid
} from '@mui/material';
import {
  TouchApp as ButtonPressIcon,
  NotificationsActive as NotificationIcon,
  CheckCircle as AcknowledgeIcon,
  LocalHospital as DispatchIcon,
  Done as FinishedIcon,
  Cancel as CancelIcon,
  Edit as StatusChangeIcon,
  Schedule as TimeIcon,
  Router as AntennaIcon,
  Error as EmergencyIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow, differenceInSeconds } from 'date-fns';

// Get icon for event type
const getEventIcon = (eventType) => {
  switch (eventType) {
    case 'button_press':
      return <ButtonPressIcon />;
    case 'notification_sent':
      return <NotificationIcon />;
    case 'status_change':
      return <StatusChangeIcon />;
    case 'acknowledged':
      return <AcknowledgeIcon />;
    case 'dispatched':
      return <DispatchIcon />;
    case 'finished':
      return <FinishedIcon />;
    case 'cancelled':
      return <CancelIcon />;
    default:
      return <TimeIcon />;
  }
};

// Get color for event type
const getEventColor = (eventType) => {
  switch (eventType) {
    case 'button_press':
      return 'error';
    case 'notification_sent':
      return 'warning';
    case 'status_change':
      return 'info';
    case 'acknowledged':
    case 'dispatched':
      return 'primary';
    case 'finished':
      return 'success';
    case 'cancelled':
      return 'default';
    default:
      return 'default';
  }
};

// Get background color for event type
const getEventBgColor = (eventType) => {
  switch (eventType) {
    case 'button_press':
      return 'error.main';
    case 'notification_sent':
      return 'warning.main';
    case 'status_change':
      return 'info.main';
    case 'acknowledged':
    case 'dispatched':
      return 'primary.main';
    case 'finished':
      return 'success.main';
    case 'cancelled':
      return 'grey.500';
    default:
      return 'grey.500';
  }
};

// Get human-readable event description
const getEventDescription = (event) => {
  switch (event.eventType) {
    case 'button_press':
      return `Emergency button pressed${event.channel ? ` (Channel ${event.channel})` : ''}`;
    case 'notification_sent':
      return 'Notification sent to emergency services';
    case 'status_change':
      return event.locationReasoning || 'Status changed';
    default:
      return event.eventType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Event';
  }
};

const HistoryTab = ({ alarm }) => {
  // Helper to get user display name
  const getUserDisplay = (event) => {
    // Check user model first
    if (event?.user?.friendlyName) return event.user.friendlyName;
    if (event?.userModel?.friendlyName) return event.userModel.friendlyName;

    // Check userId field
    if (event?.userId) return `User #${event.userId}`;
    if (event?.performedBy) return event.performedBy;

    // Check for status-specific user fields in raw data
    const rawData = event?.rawData || event?.raw || {};
    if (rawData.updatedBy) return rawData.updatedBy;
    if (rawData.acknowledgedBy?.friendlyName) return rawData.acknowledgedBy.friendlyName;
    if (rawData.dispatchedBy?.friendlyName) return rawData.dispatchedBy.friendlyName;
    if (rawData.finishedBy?.friendlyName) return rawData.finishedBy.friendlyName;

    // Check if action was from public access (no authenticated user)
    if (event?.isPublicAccess || event?.fromPublicLink) return 'Not logged in user';
    if (rawData.isPublicAccess || rawData.fromPublicLink) return 'Not logged in user';
    if (rawData.updatedBy === 'emergency_services') return 'Emergency Services (via link)';

    return null;
  };

  // Build timeline from alarm events and status changes
  const buildTimeline = () => {
    const timeline = [];

    // Add initial button press event FIRST
    if (alarm?.firstPressAt) {
      timeline.push({
        id: 'button_press_initial',
        type: 'button_press',
        timestamp: new Date(alarm.firstPressAt),
        title: 'Button Pressed',
        description: 'Emergency button was pressed',
        priority: 0, // Ensure this comes first
        details: {
          device: alarm.buttonDeviceModel?.friendlyName || alarm.Content?.buttonDeviceName || alarm.Content?.deviceName,
          person: alarm.personModel?.friendlyName
        }
      });
    }

    // Add alarm created/triggered event
    if (alarm?.createdAt || alarm?.firstPressAt) {
      const triggerTime = alarm.createdAt ? new Date(alarm.createdAt) : new Date(alarm.firstPressAt);
      // Only add if different from first press
      if (!alarm.firstPressAt || Math.abs(triggerTime - new Date(alarm.firstPressAt)) > 1000) {
        timeline.push({
          id: 'alarm_triggered',
          type: 'status_change',
          timestamp: triggerTime,
          title: 'Alarm Triggered',
          description: 'System created alarm record and started countdown',
          priority: 1,
          details: {
            location: alarm.locationModel?.friendlyName
          }
        });
      }
    }

    // Add alarm events from events array
    if (alarm?.events) {
      alarm.events.forEach((event, index) => {
        timeline.push({
          id: `event_${event.id || index}`,
          type: event.eventType,
          timestamp: new Date(event.eventTimestamp),
          title: getEventDescription(event),
          description: event.locationReasoning || null,
          user: getUserDisplay(event),
          priority: 10,
          details: {
            antenna: event.antennaName,
            rssi: event.rssiDbm,
            signalQuality: event.signalQuality,
            channel: event.channel,
            action: event.action
          },
          raw: event
        });
      });
    }

    // Add countdown end / sent to emergency
    if (alarm?.sentToEmergencyAt) {
      timeline.push({
        id: 'sent_emergency',
        type: 'notification_sent',
        timestamp: new Date(alarm.sentToEmergencyAt),
        title: 'Sent to Emergency Services',
        description: 'Countdown finished, alarm escalated to emergency services',
        user: 'System (automatic)',
        priority: 20,
        details: {
          notificationCount: alarm.notificationCount
        }
      });
    }

    // Add acknowledgement
    if (alarm?.acknowledgedAt) {
      timeline.push({
        id: 'acknowledged',
        type: 'acknowledged',
        timestamp: new Date(alarm.acknowledgedAt),
        title: 'Acknowledged',
        description: 'Alarm was acknowledged',
        user: alarm.acknowledgedBy?.friendlyName || alarm.acknowledgedByName ||
              (alarm.acknowledgedByPublic ? 'Not logged in user' : null),
        priority: 30
      });
    }

    // Add dispatch
    if (alarm?.dispatchedAt) {
      timeline.push({
        id: 'dispatched',
        type: 'dispatched',
        timestamp: new Date(alarm.dispatchedAt),
        title: 'Dispatched',
        description: 'Emergency services have been dispatched',
        user: alarm.dispatchedBy?.friendlyName || alarm.dispatchedByName ||
              (alarm.dispatchedByPublic ? 'Not logged in user' : null),
        priority: 40
      });
    }

    // Add finish
    if (alarm?.finishedAt) {
      const isCancelled = alarm?.alarmStateModel?.code === 'cancelled';
      timeline.push({
        id: 'finished',
        type: isCancelled ? 'cancelled' : 'finished',
        timestamp: new Date(alarm.finishedAt),
        title: isCancelled ? 'Cancelled' : 'Resolved',
        description: isCancelled
          ? (alarm.cancellationReason || 'Alarm was cancelled')
          : (alarm.resolutionComment || 'Alarm has been resolved'),
        user: alarm.finishedBy?.friendlyName || alarm.finishedByName ||
              (alarm.finishedByPublic ? 'Not logged in user' : null),
        priority: 50
      });
    }

    // Sort by timestamp first, then by priority for same timestamps
    timeline.sort((a, b) => {
      const timeDiff = a.timestamp - b.timestamp;
      if (Math.abs(timeDiff) < 1000) { // Within 1 second, use priority
        return (a.priority || 0) - (b.priority || 0);
      }
      return timeDiff;
    });

    return timeline;
  };

  const timeline = buildTimeline();

  // Calculate duration between events
  const getDuration = (current, previous) => {
    if (!previous) return null;
    const seconds = differenceInSeconds(current.timestamp, previous.timestamp);
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  return (
    <Box>
      {/* Summary Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Alarm Timeline Summary
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Chip
              icon={<ButtonPressIcon />}
              label={`${timeline.filter(e => e.type === 'button_press').length} Button Presses`}
              color="error"
              variant="outlined"
            />
            <Chip
              icon={<NotificationIcon />}
              label={`${alarm?.notificationCount || 0} Notifications`}
              color="warning"
              variant="outlined"
            />
            <Chip
              icon={<StatusChangeIcon />}
              label={`${timeline.filter(e => e.type === 'status_change').length} Status Changes`}
              color="info"
              variant="outlined"
            />
            {alarm?.firstPressAt && alarm?.finishedAt && (
              <Chip
                icon={<TimeIcon />}
                label={`Total Duration: ${getDuration(
                  { timestamp: new Date(alarm.finishedAt) },
                  { timestamp: new Date(alarm.firstPressAt) }
                )}`}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Timeline using Stepper */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Detailed Timeline
        </Typography>

        {timeline.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No events recorded
          </Typography>
        ) : (
          <Stepper orientation="vertical" activeStep={timeline.length}>
            {timeline.map((event, index) => {
              const duration = getDuration(event, timeline[index - 1]);

              return (
                <Step key={event.id} completed={true}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: getEventBgColor(event.type)
                        }}
                      >
                        {getEventIcon(event.type)}
                      </Avatar>
                    )}
                    optional={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {format(event.timestamp, 'HH:mm:ss')} - {format(event.timestamp, 'MMM d, yyyy')}
                        </Typography>
                        {duration && (
                          <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                            (+{duration})
                          </Typography>
                        )}
                        {event.user && (
                          <Typography
                            variant="caption"
                            sx={{
                              ml: 1,
                              color: event.user === 'Not logged in user' ? 'warning.main' : 'text.secondary',
                              fontStyle: event.user === 'Not logged in user' ? 'italic' : 'normal'
                            }}
                          >
                            by {event.user}
                          </Typography>
                        )}
                      </Box>
                    }
                  >
                    <Typography variant="subtitle1" fontWeight="medium">
                      {event.title}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Box sx={{ mb: 2 }}>
                      {event.description && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {event.description}
                        </Typography>
                      )}

                      {/* Additional details */}
                      {event.details && Object.keys(event.details).some(k => event.details[k]) && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {event.details.device && (
                            <Chip label={`Device: ${event.details.device}`} size="small" />
                          )}
                          {event.details.location && (
                            <Chip label={`Location: ${event.details.location}`} size="small" />
                          )}
                          {event.details.antenna && (
                            <Chip
                              icon={<AntennaIcon />}
                              label={event.details.antenna}
                              size="small"
                            />
                          )}
                          {event.details.rssi && (
                            <Chip
                              label={`${event.details.rssi} dBm`}
                              size="small"
                              color={event.details.rssi >= -60 ? 'success' :
                                     event.details.rssi >= -75 ? 'warning' : 'error'}
                            />
                          )}
                          {event.details.signalQuality && (
                            <Chip label={event.details.signalQuality} size="small" />
                          )}
                          {event.details.channel && (
                            <Chip label={`CH ${event.details.channel}`} size="small" />
                          )}
                          {event.details.notificationCount && (
                            <Chip
                              icon={<NotificationIcon />}
                              label={`${event.details.notificationCount} sent`}
                              size="small"
                              color="warning"
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </StepContent>
                </Step>
              );
            })}
          </Stepper>
        )}
      </Paper>
    </Box>
  );
};

export default HistoryTab;
